import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import {
  VenuesForSupplierIntegrationsQuery,
  useExportSupplierIntegrationsCsvLazyQuery,
  useSupplierIntegrationRequestsQuery,
  useVenuesForSupplierIntegrationsQuery,
} from '../../generated/graphql';
import { IntegrationRequestsTable } from './IntegrationRequestsTable';
import { RequestIntegrationsModal } from './RequestIntegrationsModal';

type Venue = VenuesForSupplierIntegrationsQuery['adminVenues'][number];

export const SupplierIntegrationsPage = () => {
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);

  const { data: venuesData, loading: venuesLoading } =
    useVenuesForSupplierIntegrationsQuery();

  const {
    data: requestsData,
    loading: requestsLoading,
    refetch: refetchRequests,
  } = useSupplierIntegrationRequestsQuery({
    variables: { venueId: selectedVenue?.id ?? '' },
    skip: !selectedVenue,
  });

  const [exportCsv, { loading: exportLoading }] =
    useExportSupplierIntegrationsCsvLazyQuery();

  const venues = useMemo(() => {
    const uniqueById = new Map(
      (venuesData?.adminVenues ?? []).map((venue) => [venue.id, venue]),
    );

    return [...uniqueById.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [venuesData]);

  const requests = requestsData?.supplierIntegrationRequests ?? [];
  const activeCount = requests.filter((r) => r.isActive).length;

  const handleExportCsv = async () => {
    const result = await exportCsv();
    const base64 = result.data?.exportSupplierIntegrationsCsv;
    if (!base64) return;

    const csvContent = atob(base64);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'supplier_integrations.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleIntegrationsInitiated = () => {
    setIsRequestModalOpen(false);
    refetchRequests();
  };

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight={600}>
          Supplier Integrations
        </Typography>
        <Tooltip title="Export all venues' integration stats as CSV">
          <Button
            variant="outlined"
            startIcon={
              exportLoading ? <CircularProgress size={16} /> : <DownloadIcon />
            }
            onClick={handleExportCsv}
            disabled={exportLoading}
          >
            Export CSV
          </Button>
        </Tooltip>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Autocomplete
              sx={{ minWidth: 360 }}
              options={venues}
              loading={venuesLoading}
              value={selectedVenue}
              onChange={(_, venue) => setSelectedVenue(venue)}
              getOptionLabel={(venue) => venue.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Venue"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {venuesLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </CardContent>
      </Card>

      {selectedVenue && (
        <Card>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Integration Requests
                </Typography>
                {!requestsLoading && (
                  <Typography variant="body2" color="text.secondary">
                    {requests.length} total · {activeCount} active
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsRequestModalOpen(true)}
              >
                Request New Integrations
              </Button>
            </Box>

            {requestsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <IntegrationRequestsTable requests={requests} />
            )}
          </CardContent>
        </Card>
      )}

      {selectedVenue && isRequestModalOpen && (
        <RequestIntegrationsModal
          open={isRequestModalOpen}
          venueId={selectedVenue.id}
          existingRequestSupplierIds={requests.map((r) => r.globalSupplierId)}
          onClose={() => setIsRequestModalOpen(false)}
          onSuccess={handleIntegrationsInitiated}
        />
      )}
    </Box>
  );
};
